import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import DataTable from '@/components/DataTable';
import type { Employee } from '@/types';

const MOCK_EMPLOYEES: Employee[] = [
  { id: '1', name: 'Alice Wang', department: 'Engineering', title: 'Staff Engineer', location: 'San Francisco', startDate: '2021-03-15', status: 'active' },
  { id: '2', name: 'Bob Martinez', department: 'Sales', title: 'VP Sales', location: 'New York', startDate: '2019-07-01', status: 'active' },
  { id: '3', name: 'Carol Singh', department: 'Product', title: 'Senior PM', location: 'Remote', startDate: '2022-01-10', status: 'active' },
  { id: '4', name: 'David Park', department: 'Engineering', title: 'Engineering Manager', location: 'Seattle', startDate: '2020-06-22', status: 'active' },
  { id: '5', name: 'Eva Thompson', department: 'Marketing', title: 'Director of Marketing', location: 'Austin', startDate: '2021-11-01', status: 'active' },
  { id: '6', name: 'Frank Liu', department: 'Engineering', title: 'Senior SWE', location: 'San Francisco', startDate: '2022-04-18', status: 'active' },
  { id: '7', name: 'Grace Kim', department: 'HR', title: 'HR Business Partner', location: 'New York', startDate: '2020-02-14', status: 'active' },
  { id: '8', name: 'Henry Okafor', department: 'Finance', title: 'Financial Analyst', location: 'Chicago', startDate: '2023-01-09', status: 'active' },
  { id: '9', name: 'Irene Costa', department: 'Operations', title: 'Operations Lead', location: 'Remote', startDate: '2021-08-30', status: 'on-leave' },
  { id: '10', name: 'James Brown', department: 'Sales', title: 'Account Executive', location: 'Boston', startDate: '2023-06-01', status: 'active' },
  { id: '11', name: 'Keiko Tanaka', department: 'Engineering', title: 'Principal Engineer', location: 'San Francisco', startDate: '2018-11-15', status: 'active' },
  { id: '12', name: 'Liam O\'Donnell', department: 'Product', title: 'Product Designer', location: 'Remote', startDate: '2022-09-12', status: 'active' },
  { id: '13', name: 'Maya Gupta', department: 'Engineering', title: 'SWE II', location: 'Seattle', startDate: '2024-02-05', status: 'active' },
  { id: '14', name: 'Nathan Fischer', department: 'Marketing', title: 'Growth Manager', location: 'Austin', startDate: '2023-03-20', status: 'active' },
  { id: '15', name: 'Olivia Russo', department: 'Sales', title: 'Sales Director', location: 'New York', startDate: '2019-12-01', status: 'active' },
  { id: '16', name: 'Patrick Müller', department: 'Engineering', title: 'DevOps Engineer', location: 'Remote', startDate: '2022-07-25', status: 'active' },
  { id: '17', name: 'Quinn Adams', department: 'HR', title: 'Recruiter', location: 'Chicago', startDate: '2023-10-14', status: 'active' },
  { id: '18', name: 'Rachel Fernandez', department: 'Finance', title: 'Controller', location: 'New York', startDate: '2020-05-11', status: 'active' },
  { id: '19', name: 'Sam Wilson', department: 'Operations', title: 'Facilities Manager', location: 'San Francisco', startDate: '2021-04-01', status: 'inactive' },
  { id: '20', name: 'Tanya Ivanova', department: 'Engineering', title: 'QA Lead', location: 'Remote', startDate: '2022-12-01', status: 'active' },
];

const DEPARTMENTS = ['All', 'Engineering', 'Sales', 'Product', 'Marketing', 'HR', 'Finance', 'Operations'];

const statusBadge = (status: string) => {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-600',
    'on-leave': 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] || styles.active}`}>
      {status === 'on-leave' ? 'On Leave' : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const columns = [
  { key: 'name', header: 'Name' },
  { key: 'department', header: 'Department' },
  { key: 'title', header: 'Title' },
  { key: 'location', header: 'Location' },
  { key: 'startDate', header: 'Start Date' },
  {
    key: 'status',
    header: 'Status',
    render: (item: Record<string, unknown>) => statusBadge(item.status as string),
  },
];

export default function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');

  const filtered = useMemo(() => {
    return MOCK_EMPLOYEES.filter((emp) => {
      const matchesSearch =
        search === '' ||
        emp.name.toLowerCase().includes(search.toLowerCase()) ||
        emp.title.toLowerCase().includes(search.toLowerCase()) ||
        emp.location.toLowerCase().includes(search.toLowerCase());
      const matchesDept =
        deptFilter === 'All' || emp.department === deptFilter;
      return matchesSearch && matchesDept;
    });
  }, [search, deptFilter]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage your workforce directory
        </p>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10"
          />
        </div>
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="input-field w-full sm:w-48"
        >
          {DEPARTMENTS.map((dept) => (
            <option key={dept} value={dept}>
              {dept === 'All' ? 'All Departments' : dept}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={filtered as unknown as Record<string, unknown>[]}
        pageSize={10}
      />
    </div>
  );
}
